import { CropConfigV2 } from "@redotech/redo-content-builder-common/crop-config-v2";
import { SpellCheckImageError } from "@redotech/redo-content-builder-common/spell-check-image-error";
import { DiscountGroupType } from "@redotech/redo-discounts-common/discount";
import {
  RedoDiscount,
  RedoDraftDiscount,
} from "@redotech/redo-discounts-common/discount/discount-db-parser";
import { SchemaType } from "@redotech/redo-flows-common/schemas/schemas";
import { SchemaField } from "@redotech/redo-flows-core-schema-common/type-system/schema";
import { ScreenSize } from "@redotech/redo-marketing-templates-common/email-builder";
import {
  GetEmailRecommendedInteractiveProductsParams,
  GetEmailRecommendedStaticVariantsParams,
  GmailAnnotatedEmailRecommendedVariant,
  InteractiveCartProduct,
} from "@redotech/redo-marketing-templates-common/email-product-recommendation";
import {
  EmailBlock,
  EmailTemplate,
} from "@redotech/redo-marketing-templates-common/email-template";
import { EmailBuilderMetafield } from "@redotech/redo-marketing-templates-common/metafield";
import { SavedEmailSection } from "@redotech/redo-marketing-templates-common/saved-email-section";
import { PreviewEventListItem } from "@redotech/redo-merchant-marketing-rpc/schema/email-templates/list-preview-events";
import {
  ImageSearchInput,
  ImageSearchResult,
} from "@redotech/redo-merchant-marketing-rpc/schema/product-images/get-product-images";
import { Provider } from "@redotech/redo-order-common/provider";
import {
  ProductInfo,
  RedoProductData,
} from "@redotech/redo-product-common/product";
import { Team } from "@redotech/redo-team-common/team";
import { ShopifyStorefrontClient } from "@redotech/shopify-storefront";
import { UseQueryResult } from "@tanstack/react-query";
import { createContext } from "react";
import { ProductFilterContext } from "../product-filter/product-filter-context";

/**
 * This context is only accessible in the email builder, not when rendering a production email.
 * Anything that is needed in both the builder AND production email rendering should go in EmailRenderContext instead.
 */
export interface EmailBuilderContext {
  team?: Team;
  template: EmailTemplate;
  storefrontClient: ShopifyStorefrontClient | null;
  screenSize: ScreenSize;
  /** Whether the dynamic table block is shown in the block picker (LaunchDarkly
   * email-table-block flag, evaluated per team where the builder is mounted). */
  emailTableBlockEnabled?: boolean;
  /**
   * The actual viewport width for ScreenSize.CUSTOM.
   * Only used when screenSize === ScreenSize.CUSTOM for testing responsive behavior.
   * DESKTOP and MOBILE use hardcoded widths (600px and 375px).
   */
  customViewportWidth?: number;
  setScreenSize: (screenSize: ScreenSize) => void;
  setTemplate: (template: EmailTemplate) => void;
  getCollections: (
    searchQuery?: string,
  ) => Promise<{ id: string; name: string }[]>;
  getMetafields: () => Promise<EmailBuilderMetafield[]>;
  getProduct: (
    productId: string,
    provider: Provider,
  ) => Promise<RedoProductData | null | undefined>;
  searchProducts: ({
    search,
    provider,
    signal,
  }: {
    search: string;
    provider: Provider;
    signal: AbortSignal;
  }) => Promise<ProductInfo[] | undefined>;
  sendTestEmail: (args: {
    emailAddresses: string[];
    template: EmailTemplate;
    orderNumber?: string;
    eventId?: string;
    signal?: AbortSignal;
  }) => Promise<void>;
  /** The flow trigger's event name (threaded via the builder URL) to preview
   * against. Absent when the template isn't opened from a custom-event flow. */
  previewEventName?: string;
  /** Recent events (from Snowflake, hydrated from Mongo) to preview against. */
  listPreviewEvents?: (args: {
    eventType: SchemaType;
    eventName: string;
    limit?: number;
    signal?: AbortSignal;
  }) => Promise<PreviewEventListItem[]>;
  /** Server-rendered email HTML for a template, optionally against an event. */
  renderPreviewHtml?: (args: {
    template: EmailTemplate;
    eventId?: string;
    signal?: AbortSignal;
  }) => Promise<string>;
  getDiscounts: (groupType?: DiscountGroupType) => Promise<RedoDiscount[]>;
  getDiscount: (id: string) => Promise<RedoDiscount | undefined>;
  createDiscount: ({
    discountConfiguration,
  }: {
    discountConfiguration: RedoDraftDiscount;
  }) => Promise<string>;
  updateDiscount: ({
    discountId,
    discountConfiguration,
  }: {
    discountId: string;
    discountConfiguration: RedoDraftDiscount;
  }) => Promise<void>;
  deleteDiscount: (id: string) => Promise<void>;
  searchImages: (
    input: ImageSearchInput,
    options?: { signal?: AbortSignal },
  ) => Promise<ImageSearchResult>;
  getStaticVariantRecommendations: (
    params: GetEmailRecommendedStaticVariantsParams,
  ) => Promise<GmailAnnotatedEmailRecommendedVariant[]>;
  getInteractiveProductRecommendations: (
    params: GetEmailRecommendedInteractiveProductsParams,
  ) => Promise<InteractiveCartProduct[]>;
  countBytes: (
    template: EmailTemplate,
    abortSignal?: AbortSignal,
  ) => Promise<number>;
  getSavedEmailSections: () => Promise<SavedEmailSection[]>;
  getSavedEmailSectionById: ({
    sectionId,
    includeArchived,
  }: {
    sectionId: string;
    includeArchived?: boolean;
  }) => Promise<SavedEmailSection | null>;
  createSavedEmailSection: (params: {
    name: string;
    blocks: EmailBlock[];
  }) => Promise<SavedEmailSection>;
  updateSavedEmailSection: (params: {
    sectionId: string;
    updates: { name?: string; blocks?: EmailBlock[] };
  }) => Promise<SavedEmailSection>;
  archiveSavedEmailSection: (
    sectionId: string,
  ) => Promise<{ success: boolean }>;
  useTeamMarketingProviders: () => UseQueryResult<
    { default: Provider; available: Provider[] },
    Error
  >;
  useTeamProviders: () => UseQueryResult<
    { default: Provider; available: Provider[] },
    Error
  >;
  productFilterContext: ProductFilterContext | null;
  cropImage?: (
    imageUrl: string,
    cropConfig: CropConfigV2,
  ) => Promise<{ imageUrl: string }>;
  editImageWithAi?: (params: {
    imageUrl: string;
    prompt: string;
  }) => Promise<{ imageUrl: string }>;
  spellCheckImage?: (imageUrl: string) => Promise<SpellCheckImageError[]>;
  getOriginalImageUrl?: (
    imageUrl: string,
  ) => Promise<{ originalImageUrl: string | null }>;
  getProductImages?: (
    input: ImageSearchInput,
    options?: { signal?: AbortSignal },
  ) => Promise<ImageSearchResult>;
  getAiSubjectLineSuggestions?: (params: {
    text: string;
    tone?: string;
  }) => Promise<{ text: string; tone: string }[]>;
  getAiEmailPreviewSuggestions?: (params: {
    text: string;
    tone?: string;
    subjectLine?: string;
  }) => Promise<{ text: string; tone: string }[]>;
  /**
   * Optional pre-filtered dynamic variables for editors.
   * When provided, editors will use these instead of computing from schema.
   * This allows filtering by team integrations (e.g., Loyalty Lion fields only appear if integration is enabled).
   */
  availableDynamicVariables: readonly SchemaField[];
  forceRehydrateBlock?: (blockId: string) => void;
  blockRehydrationCounters?: Record<string, number>;
}

export const EmailBuilderContext = createContext<
  EmailBuilderContext | undefined
>(undefined);
